import { useT, useI18n } from '../../i18n/useT'
import { UI_LOCALES } from '../../i18n/config'

interface NavLink {
  label: string
  href: string
  external?: boolean
}

interface MobileMenuProps {
  isOpen: boolean
  onClose: () => void
  navLinks: NavLink[]
  isAuthenticated: boolean
  userEmail?: string
  credits?: number
  monthlyCredits?: boolean
  onLogin: () => void
  onLogout: () => void
}

export function MobileMenu({
  isOpen,
  onClose,
  navLinks,
  isAuthenticated,
  userEmail,
  credits,
  monthlyCredits,
  onLogin,
  onLogout,
}: MobileMenuProps) {
  const t = useT()
  const { locale, setLocale } = useI18n()
  if (!isOpen) return null

  return (
    <div className="lg:hidden border-t border-border bg-surface">
      <div className="px-4 py-3 space-y-1">
        {navLinks.map((link) => (
          <a
            key={link.label}
            href={link.href}
            target={link.external ? '_blank' : undefined}
            rel={link.external ? 'noopener noreferrer' : undefined}
            onClick={onClose}
            className="flex w-full items-center gap-1.5 px-3 py-2.5 text-sm text-text-secondary hover:text-text rounded-lg hover:bg-surface-hover transition-colors"
          >
            {link.label}
            {link.external && (
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 19.5l15-15m0 0H8.25m11.25 0v11.25" />
              </svg>
            )}
          </a>
        ))}

        {/* Interface language — a native select works best on touch */}
        <div className="flex items-center justify-between gap-3 px-3 py-2">
          <span className="text-sm text-text-secondary">{t('ui.language')}</span>
          <select
            value={locale}
            onChange={(e) => setLocale(e.target.value)}
            className="text-sm text-text bg-surface border border-border rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-border-focus"
          >
            {UI_LOCALES.map((l) => (
              <option key={l.code} value={l.code}>
                {l.label}
              </option>
            ))}
          </select>
        </div>

        <div className="border-t border-border pt-2 mt-2">
          {isAuthenticated ? (
            <>
              <div className="px-3 py-2">
                <div className="text-xs text-text-muted truncate">{userEmail}</div>
                {credits !== undefined && (
                  <div className="mt-1 text-sm font-medium text-text">
                    {monthlyCredits
                      ? credits === 1
                        ? t('credits.remainingMonthlyOne')
                        : t('credits.remainingMonthly', { count: credits })
                      : credits === 1
                        ? t('credits.remainingOne')
                        : t('credits.remaining', { count: credits })}
                  </div>
                )}
              </div>
              {/* Saved transcriptions live right under the balance they belong to */}
              <a
                href="/history"
                onClick={onClose}
                className="block w-full px-3 py-2.5 text-sm text-text hover:bg-surface-hover rounded-lg transition-colors"
              >
                {t('nav.history')}
              </a>
              <button
                onClick={() => { onLogout(); onClose() }}
                className="block w-full text-left px-3 py-2.5 text-sm text-text hover:bg-surface-hover rounded-lg transition-colors"
              >
                {t('nav.logout')}
              </button>
            </>
          ) : (
            <>
              {credits !== undefined && (
                <div className="px-3 py-2 text-sm text-text-secondary">
                  {credits === 1 ? t('credits.freeRemainingOne') : t('credits.freeRemaining', { count: credits })}
                </div>
              )}
              <a
                href="/history"
                onClick={onClose}
                className="block w-full px-3 py-2.5 text-sm text-text hover:bg-surface-hover rounded-lg transition-colors"
              >
                {t('nav.history')}
              </a>
              <button
                onClick={() => { onLogin(); onClose() }}
                className="block w-full text-left px-3 py-2.5 text-sm font-medium text-primary hover:bg-primary/5 rounded-lg transition-colors"
              >
                {t('nav.login')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
